import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

let keyCounter = 0

/**
 * Keys for a bone list whose length can change (skeletons scale their counts to
 * the measured space). The list only ever grows, so keys already handed out keep
 * their identity and React reuses those DOM nodes across a resize.
 */
export function useStableKeys(count: number): string[] {
	const safeCount = Math.max(0, count)
	const cacheRef = useRef<string[]>([])

	if (cacheRef.current.length < safeCount) {
		const grown = cacheRef.current.slice()
		while (grown.length < safeCount) {
			grown.push(`sk-${keyCounter++}`)
		}
		cacheRef.current = grown
	}

	return useMemo(() => cacheRef.current.slice(0, safeCount), [safeCount])
}

export interface ElementSize {
	width: number
	height: number
}

/**
 * Measures the element it is attached to. Skeletons use it to scale their bone
 * counts to the space they actually get (a 2-row dashboard panel and a
 * full-page panel must not render the same fixed skeleton).
 *
 * Attach only to roots that cannot grow with their children (`size-full` +
 * `overflow-hidden`), otherwise the count → height → count loop never settles.
 */
export function useElementSize<T extends HTMLElement>(): [(node: T | null) => void, ElementSize] {
	const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 })
	const observerRef = useRef<ResizeObserver | null>(null)

	const ref = useCallback((node: T | null) => {
		observerRef.current?.disconnect()
		observerRef.current = null
		if (!node) {
			return
		}

		// Measured during commit, so the first paint already has the right counts.
		setSize({ width: node.clientWidth, height: node.clientHeight })
		// jsdom (tests) has no ResizeObserver — the one-shot measurement above is enough there.
		if (typeof ResizeObserver === 'undefined') {
			return
		}

		const observer = new ResizeObserver(entries => {
			const rect = entries[0]?.contentRect
			if (!rect) {
				return
			}
			// Sub-pixel jitter would otherwise re-key every bone on each frame.
			setSize(prev =>
				Math.abs(prev.width - rect.width) < 1 && Math.abs(prev.height - rect.height) < 1
					? prev
					: { width: rect.width, height: rect.height },
			)
		})
		observer.observe(node)
		observerRef.current = observer
	}, [])

	useEffect(() => () => observerRef.current?.disconnect(), [])

	return [ref, size]
}

/** How many `unit`-sized bones fit in `available` px, clamped to [min, max]. Falls back to `min` before measurement. */
export function fitCount(available: number, unit: number, min: number, max: number): number {
	if (!(available > 0) || !(unit > 0)) {
		return min
	}
	return Math.min(max, Math.max(min, Math.floor(available / unit)))
}

export interface SkeletonRegionProps {
	/** Pass `false` to render decorative bones only (when composing skeletons). */
	label?: string | false
	className?: string
	ref?: React.Ref<HTMLDivElement>
	dataTestId?: string
	children: React.ReactNode
}

export function SkeletonRegion({ label = 'Loading', className, ref, dataTestId, children }: SkeletonRegionProps) {
	if (label === false) {
		return (
			<div ref={ref} className={className} data-testid={dataTestId}>
				{children}
			</div>
		)
	}
	return (
		// biome-ignore lint/a11y/useSemanticElements: skeletons wrap block-level layout (tables/grids/cards) which <output> (phrasing content only) cannot legally contain, so role='status' on a div is the correct live-region pattern
		<div ref={ref} role='status' aria-busy='true' aria-live='polite' className={className} data-testid={dataTestId}>
			<span className='sr-only'>{label}</span>
			{children}
		</div>
	)
}
